/**
 * AssemblyAI streaming adapter — the ONE module that knows the vendor's
 * WebSocket URL, token auth, and `Turn` message shape (Rule 7).
 *
 * The socket is opened straight from the browser with the short-lived token
 * minted by media-v3 (`?token=` query param). Audio goes up as binary frames of
 * raw PCM16 little-endian mono at the declared sample rate — this path takes no
 * containerized audio, so it is fed by the PCM tap (`../pcm16-capture.ts`), not
 * by the recorder's WebM/Opus stream.
 *
 * Turn semantics: every `Turn` carries the WHOLE turn so far and supersedes the
 * previous one, so partials REPLACE the pending tail; `end_of_turn` commits.
 * Universal-3.5 Pro sends one (formatted) end-of-turn per turn; the older
 * `universal-streaming-*` models send an unformatted one and then, with
 * `format_turns`, a formatted copy for the SAME `turn_order` — the second is
 * mapped to a `replacesLast` final so a turn is never appended twice.
 */

import type { SttGrant } from "../../_types/media-upload";
import type { TranscriptEvent } from "./transcript-merge";

const STREAM_URL = "wss://streaming.assemblyai.com/v3/ws";
/** Raw 16-bit little-endian PCM — what the PCM tap produces. */
const ENCODING = "pcm_s16le";
/** A handshake that has not completed by then is abandoned (the owner may reconnect). */
const CONNECT_TIMEOUT_MS = 10_000;

/** The `Turn` fields the adapter reads, normalized. */
export interface AssemblyAiTurn {
  turnOrder: number;
  text: string;
  endOfTurn: boolean;
}

export interface AssemblyAiSocketHandlers {
  onEvent: (event: TranscriptEvent) => void;
  onOpen: () => void;
  /** `clean` is true when WE ended it (`Terminate` sent, vendor flushed). */
  onClose: (info: { clean: boolean; code: number; reason: string }) => void;
}

export interface AssemblyAiSocket {
  send: (frame: ArrayBuffer) => void;
  /** Ask the recognizer to flush; the socket closes once trailing finals land. */
  finish: () => void;
  /** Drop the connection without waiting for trailing results. */
  abort: () => void;
}

/** Exported for its unit test — the URL is the whole vendor contract. */
export function streamUrl(grant: SttGrant, sampleRate: number): string {
  const query = new URLSearchParams({
    token: grant.token,
    speech_model: grant.model,
    encoding: ENCODING,
    sample_rate: String(sampleRate),
    // The vendor takes the term list as a JSON-stringified array — a comma list is
    // refused with error 3006 (verified live, 2026-08-26).
    ...(grant.keyterms && grant.keyterms.length > 0
      ? { keyterms_prompt: JSON.stringify(grant.keyterms) }
      : {}),
  });
  return `${STREAM_URL}?${query.toString()}`;
}

function parseJson(raw: string): Record<string, unknown> | null {
  try {
    const value: unknown = JSON.parse(raw);
    return value && typeof value === "object"
      ? (value as Record<string, unknown>)
      : null;
  } catch {
    return null;
  }
}

/** A `Turn` frame → its normalized fields; every other frame (and garbage) → null. */
export function parseAssemblyAiTurn(raw: string): AssemblyAiTurn | null {
  const message = parseJson(raw);
  if (message?.type !== "Turn") return null;
  return {
    turnOrder: typeof message.turn_order === "number" ? message.turn_order : -1,
    text: typeof message.transcript === "string" ? message.transcript : "",
    endOfTurn: message.end_of_turn === true,
  };
}

/**
 * Pure. Maps a turn onto the provider-agnostic event stream given the last
 * turn that was committed: a second end-of-turn for that same turn is a
 * revision of it, not a new segment.
 */
export function turnToTranscriptEvent(
  turn: AssemblyAiTurn,
  lastCommittedTurn: number | null,
): TranscriptEvent {
  if (!turn.endOfTurn) return { kind: "interim", text: turn.text };
  // An unknown turn_order (-1) can never be "the same turn again".
  if (turn.turnOrder >= 0 && turn.turnOrder === lastCommittedTurn) {
    return { kind: "final", text: turn.text, replacesLast: true };
  }
  return { kind: "final", text: turn.text };
}

/** The vendor's error frame text, if this is one (`{ type: "Error", error }`). */
function errorFrameText(raw: string): string | null {
  const message = parseJson(raw);
  if (!message) return null;
  const detail = message.error ?? message.message;
  if (message.type === "Error" || typeof message.error === "string") {
    return typeof detail === "string" ? detail : "vendor error";
  }
  return null;
}

export function openAssemblyAiSocket(
  grant: SttGrant,
  sampleRate: number,
  handlers: AssemblyAiSocketHandlers,
): AssemblyAiSocket {
  const ws = new WebSocket(streamUrl(grant, sampleRate));
  let finishing = false;
  let errored = false;
  let vendorError: string | null = null;
  let lastCommittedTurn: number | null = null;
  const connectTimer = setTimeout(() => {
    if (ws.readyState !== WebSocket.CONNECTING) return;
    vendorError = "connect timeout";
    ws.close();
  }, CONNECT_TIMEOUT_MS);

  ws.onopen = () => {
    clearTimeout(connectTimer);
    handlers.onOpen();
  };
  ws.onmessage = (e) => {
    if (typeof e.data !== "string") return;
    const turn = parseAssemblyAiTurn(e.data);
    if (turn) {
      handlers.onEvent(turnToTranscriptEvent(turn, lastCommittedTurn));
      if (turn.endOfTurn) lastCommittedTurn = turn.turnOrder;
      return;
    }
    const message = parseJson(e.data);
    // The vendor acknowledges `Terminate` with a Termination frame and then
    // closes; closing from our side too makes the wait deterministic.
    if (message?.type === "Termination") {
      ws.close();
      return;
    }
    vendorError = errorFrameText(e.data) ?? vendorError;
  };
  ws.onclose = (e) => {
    clearTimeout(connectTimer);
    handlers.onClose({
      clean: finishing,
      code: e.code,
      reason: vendorError || e.reason || (errored ? "connection error" : ""),
    });
  };
  // `onerror` always precedes `onclose` and carries no detail; remember it so
  // the close reason distinguishes "vendor closed" from "could not connect".
  ws.onerror = () => {
    errored = true;
  };

  return {
    send(frame) {
      if (ws.readyState === WebSocket.OPEN) ws.send(frame);
    },
    finish() {
      finishing = true;
      if (ws.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify({ type: "Terminate" }));
      } else {
        ws.close();
      }
    },
    abort() {
      finishing = true;
      ws.close();
    },
  };
}
